import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { hämtaKunder, hämtaViner, hämtaInkopare, skapaMote, skapaInkopare, skapaKund, AktivitetsTyp, Kund, Vin, Inkopare } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { TEMA } from '../theme';
import SokbarLista from '../components/SokbarLista';

interface Provning {
  vin_id: string;
  reaktion: string;
  kommentar: string;
  pris_accepterat: string;
}

interface NyKund {
  namn: string;
  typ: string;
  segment: string;
  stad: string;
  matkoncept: string;
}

interface NyInkopare {
  namn: string;
  roll: string;
  smakprofil_text: string;
  föredrar_typ: string;
  prisnivå: string;
  telefon: string;
  email: string;
}

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '16px',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  color: TEMA.inkFaint,
  display: 'block',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
  minWidth: 0,
};

const underKortStyle: React.CSSProperties = {
  background: TEMA.bg,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '6px',
  padding: '14px',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

function typKnappStyle(aktiv: boolean): React.CSSProperties {
  return {
    padding: '9px 18px',
    borderRadius: '2px',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    fontWeight: 500,
    border: `1px solid ${aktiv ? TEMA.accent : TEMA.border}`,
    cursor: 'pointer',
    background: aktiv ? TEMA.accent : 'transparent',
    color: aktiv ? TEMA.onAccent : TEMA.inkSoft,
  };
}

const TYP_ALTERNATIV: { värde: Exclude<AktivitetsTyp, 'epost'>; etikett: string }[] = [
  { värde: 'möte', etikett: 'Möte' },
  { värde: 'samtal', etikett: 'Samtal' },
  { värde: 'anteckning', etikett: 'Anteckning' },
  { värde: 'paminnelse', etikett: 'Påminnelse' },
];

function dagensDatumLokalt(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const NOTAT_PLACEHOLDER: Record<string, string> = {
  möte: 'Anteckningar från mötet...',
  samtal: 'Vad sas i samtalet?',
  anteckning: 'Vad vill du notera?',
  paminnelse: 'Vad ska du komma ihåg?',
};

export default function RegistreraAktivitet() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profil } = useAuth();
  const [kunder, setKunder] = useState<Kund[]>([]);
  const [viner, setViner] = useState<Vin[]>([]);
  const [inkopare, setInkopare] = useState<Inkopare[]>([]);
  const [sparar, setSparar] = useState(false);
  const [sparat, setSparat] = useState(false);
  const [visaNyInkopare, setVisaNyInkopare] = useState(false);
  const [visaNyKund, setVisaNyKund] = useState(false);

  const [aktivitetsTyp, setAktivitetsTyp] = useState<Exclude<AktivitetsTyp, 'epost'>>('möte');

  const [nyKund, setNyKund] = useState<NyKund>({
    namn: '', typ: 'restaurang', segment: 'B', stad: '', matkoncept: ''
  });

  const [nyInkopare, setNyInkopare] = useState<NyInkopare>({
    namn: '', roll: '', smakprofil_text: '', föredrar_typ: '', prisnivå: 'medel', telefon: '', email: ''
  });

  const [form, setForm] = useState({
    kund_id: searchParams.get('kund_id') || '',
    inkopare_id: '',
    säljare: profil?.namn || '',
    datum: dagensDatumLokalt(),
    nästa_kontakt: '',
    notat: '',
  });

  const [provningar, setProvningar] = useState<Provning[]>([
    { vin_id: '', reaktion: 'neutral', kommentar: '', pris_accepterat: 'vet ej' }
  ]);

  useEffect(() => {
    Promise.all([hämtaKunder(), hämtaViner(), hämtaInkopare()])
      .then(([k, v, i]) => {
        setKunder(k.data);
        setViner(v.data);
        setInkopare(i.data);
      })
      .catch((err) => {
        console.error('Kunde inte ladda formulärdata:', err);
        alert('Kunde inte ladda kunder/viner/inköpare. Ladda om sidan och försök igen.');
      });
  }, []);

  const filtreradeInkopare = inkopare.filter(i => i.kund_id === form.kund_id);

  function hanteraKundVal(värde: string) {
    if (värde === '__ny__') {
      setVisaNyKund(true);
      setVisaNyInkopare(true);
      setForm(f => ({ ...f, kund_id: '', inkopare_id: '' }));
    } else {
      setVisaNyKund(false);
      setForm(f => ({ ...f, kund_id: värde, inkopare_id: '' }));
    }
  }

  function hanteraInkopareVal(värde: string) {
    if (värde === '__ny__') {
      setVisaNyInkopare(true);
      setForm(f => ({ ...f, inkopare_id: '' }));
    } else {
      setVisaNyInkopare(false);
      setForm(f => ({ ...f, inkopare_id: värde }));
    }
  }

  function uppdateraProvning(index: number, fält: keyof Provning, värde: string) {
    setProvningar(prev => prev.map((p, i) => i === index ? { ...p, [fält]: värde } : p));
  }

  function läggTillProvning() {
    setProvningar(prev => [...prev, { vin_id: '', reaktion: 'neutral', kommentar: '', pris_accepterat: 'vet ej' }]);
  }

  function taBortProvning(index: number) {
    setProvningar(prev => prev.filter((_, i) => i !== index));
  }

  async function spara() {
    if (aktivitetsTyp === 'paminnelse' && !form.nästa_kontakt) {
      alert('Ange ett datum för när påminnelsen ska dyka upp.');
      return;
    }
    setSparar(true);
    try {
      let kund_id = form.kund_id;

      if (visaNyKund && nyKund.namn) {
        const res = await skapaKund({
          namn: nyKund.namn,
          typ: nyKund.typ,
          segment: nyKund.segment,
          stad: nyKund.stad,
          matkoncept: nyKund.matkoncept,
          ansvarig_säljare: profil?.namn || '',
          aktiv: true,
        });
        kund_id = res.data.kund_id;
      }

      let inkopare_id = form.inkopare_id;

      if (visaNyInkopare && nyInkopare.namn) {
        const res = await skapaInkopare({
          kund_id,
          namn: nyInkopare.namn,
          roll: nyInkopare.roll,
          smakprofil_text: nyInkopare.smakprofil_text,
          föredrar_typ: nyInkopare.föredrar_typ,
          prisnivå: nyInkopare.prisnivå,
          telefon: nyInkopare.telefon,
          email: nyInkopare.email,
        });
        inkopare_id = res.data.inkopare_id;
      }

      const provningarMappade = aktivitetsTyp === 'möte'
        ? provningar.map(p => ({
            vin_namn: viner.find(v => v.vin_id === p.vin_id)?.namn || p.vin_id,
            reaktion: p.reaktion,
            kommentar: p.kommentar,
            pris_accepterat: p.pris_accepterat === 'ja' ? true : p.pris_accepterat === 'nej' ? false : null,
          }))
        : [];

      await skapaMote({ ...form, kund_id, inkopare_id: inkopare_id || null, typ: aktivitetsTyp, provningar: provningarMappade });
      setSparat(true);
      setTimeout(() => navigate('/idag'), 1500);
    } catch (e) {
      alert('Något gick fel – försök igen');
    } finally {
      setSparar(false);
    }
  }

  if (sparat) return (
    <div style={{ textAlign: 'center', marginTop: '64px' }}>
      <p style={{ fontFamily: "'Fraunces', serif", fontSize: '20px', color: TEMA.ink, margin: '0 0 8px' }}>Aktiviteten är sparad!</p>
      <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>Skickar dig tillbaka...</p>
    </div>
  );

  const visaMötesfält = aktivitetsTyp === 'möte';
  const visaPåminnelsefält = aktivitetsTyp === 'paminnelse';

  return (
    <div>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: '0 0 20px' }}>Registrera aktivitet</h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
        {TYP_ALTERNATIV.map(t => (
          <button key={t.värde} onClick={() => setAktivitetsTyp(t.värde)} style={typKnappStyle(aktivitetsTyp === t.värde)}>
            {t.etikett}
          </button>
        ))}
      </div>

      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>
          {visaMötesfält ? 'Mötesinfo' : 'Info'}
        </h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Restaurang</label>
          <SokbarLista
            värde={visaNyKund ? '__ny__' : form.kund_id}
            onChange={hanteraKundVal}
            placeholder="Välj restaurang..."
            alternativ={[
              ...kunder.map(k => ({ värde: k.kund_id, etikett: k.namn, under: k.stad || undefined })),
              { värde: '__ny__', etikett: '+ Lägg till ny kund' },
            ]}
          />

          {visaNyKund && (
            <div style={{ ...underKortStyle, marginTop: '12px' }}>
              <p style={labelStyle}>Ny kund</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px', marginBottom: '10px' }}>
                <div>
                  <label style={labelStyle}>Namn</label>
                  <input style={inputStyle} value={nyKund.namn} onChange={e => setNyKund(n => ({ ...n, namn: e.target.value }))} placeholder="Restaurangens namn" />
                </div>
                <div>
                  <label style={labelStyle}>Typ</label>
                  <select style={inputStyle} value={nyKund.typ} onChange={e => setNyKund(n => ({ ...n, typ: e.target.value }))}>
                    <option value="restaurang">Restaurang</option>
                    <option value="hotell">Hotell</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px', marginBottom: '10px' }}>
                <div>
                  <label style={labelStyle}>Stad</label>
                  <input style={inputStyle} value={nyKund.stad} onChange={e => setNyKund(n => ({ ...n, stad: e.target.value }))} placeholder="Stockholm" />
                </div>
                <div>
                  <label style={labelStyle}>Segment</label>
                  <select style={inputStyle} value={nyKund.segment} onChange={e => setNyKund(n => ({ ...n, segment: e.target.value }))}>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Matkoncept</label>
                <input style={inputStyle} value={nyKund.matkoncept} onChange={e => setNyKund(n => ({ ...n, matkoncept: e.target.value }))} placeholder="T.ex. nordisk finedining, bistro..." />
              </div>
            </div>
          )}
        </div>

        {(form.kund_id || visaNyKund) && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Inköpare (valfritt)</label>
            {!visaNyKund && (
              <div style={{ marginBottom: '10px' }}>
                <SokbarLista
                  värde={visaNyInkopare ? '__ny__' : form.inkopare_id}
                  onChange={hanteraInkopareVal}
                  placeholder="Välj inköpare..."
                  alternativ={[
                    ...filtreradeInkopare.map(i => ({
                      värde: (i.inkopare_id || i.inköpare_id) as string,
                      etikett: i.namn,
                      under: i.roll || undefined,
                    })),
                    { värde: '__ny__', etikett: '+ Lägg till ny inköpare' },
                  ]}
                />
              </div>
            )}

            {visaNyInkopare && (
              <div style={{ ...underKortStyle, marginTop: '4px' }}>
                <p style={labelStyle}>
                  {visaNyKund ? 'Kontaktperson' : 'Ny inköpare'}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={labelStyle}>Namn</label>
                    <input style={inputStyle} value={nyInkopare.namn} onChange={e => setNyInkopare(n => ({ ...n, namn: e.target.value }))} placeholder="För- och efternamn" />
                  </div>
                  <div>
                    <label style={labelStyle}>Roll</label>
                    <input style={inputStyle} value={nyInkopare.roll} onChange={e => setNyInkopare(n => ({ ...n, roll: e.target.value }))} placeholder="Sommelier, F&B-chef..." />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={labelStyle}>Telefon</label>
                    <input style={inputStyle} value={nyInkopare.telefon} onChange={e => setNyInkopare(n => ({ ...n, telefon: e.target.value }))} placeholder="070-000 00 00" />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input type="email" style={inputStyle} value={nyInkopare.email} onChange={e => setNyInkopare(n => ({ ...n, email: e.target.value }))} placeholder="namn@restaurang.se" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={labelStyle}>Föredrar typ</label>
                    <select style={inputStyle} value={nyInkopare.föredrar_typ} onChange={e => setNyInkopare(n => ({ ...n, föredrar_typ: e.target.value }))}>
                      <option value="">Vet ej</option>
                      <option value="rött">Rött</option>
                      <option value="vitt">Vitt</option>
                      <option value="rosé">Rosé</option>
                      <option value="mousserande">Mousserande</option>
                      <option value="naturvin">Naturvin</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Prisnivå</label>
                    <select style={inputStyle} value={nyInkopare.prisnivå} onChange={e => setNyInkopare(n => ({ ...n, prisnivå: e.target.value }))}>
                      <option value="låg">Låg</option>
                      <option value="medel">Medel</option>
                      <option value="hög">Hög</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Smakprofil (fritext)</label>
                  <input style={inputStyle} value={nyInkopare.smakprofil_text} onChange={e => setNyInkopare(n => ({ ...n, smakprofil_text: e.target.value }))} placeholder="T.ex. gillar mineraliska vita..." />
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '14px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Säljare</label>
            <input style={inputStyle} value={form.säljare} onChange={e => setForm(f => ({ ...f, säljare: e.target.value }))} placeholder="Ditt namn" />
          </div>
          <div>
            <label style={labelStyle}>Datum</label>
            <input type="date" style={inputStyle} value={form.datum} onChange={e => setForm(f => ({ ...f, datum: e.target.value }))} />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>{visaPåminnelsefält ? 'Påminn mig den' : 'Nästa kontakt (valfritt)'}</label>
          <input type="date" style={inputStyle} value={form.nästa_kontakt} onChange={e => setForm(f => ({ ...f, nästa_kontakt: e.target.value }))} />
        </div>

        <div>
          <label style={labelStyle}>{visaPåminnelsefält ? 'Påminnelsetext' : 'Notat'}</label>
          <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} value={form.notat} onChange={e => setForm(f => ({ ...f, notat: e.target.value }))} placeholder={NOTAT_PLACEHOLDER[aktivitetsTyp]} />
        </div>
      </div>

      {visaMötesfält && (
        <div style={kortStyle}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Provade viner</h2>

          {provningar.map((p, index) => (
            <div key={index} style={{ ...underKortStyle, marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: '14px', color: TEMA.ink }}>Vin {index + 1}</span>
                {provningar.length > 1 && (
                  <button onClick={() => taBortProvning(index)} style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkFaint, background: 'none', border: 'none', cursor: 'pointer' }}>Ta bort</button>
                )}
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={labelStyle}>Vin</label>
                <SokbarLista
                  värde={p.vin_id}
                  onChange={v => uppdateraProvning(index, 'vin_id', v)}
                  placeholder="Välj vin..."
                  alternativ={viner.map(v => ({
                    värde: v.vin_id,
                    etikett: v.namn,
                    under: v.producent || undefined,
                  }))}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px', marginBottom: '10px' }}>
                <div>
                  <label style={labelStyle}>Reaktion</label>
                  <select style={inputStyle} value={p.reaktion} onChange={e => uppdateraProvning(index, 'reaktion', e.target.value)}>
                    <option value="gillade">Gillade</option>
                    <option value="neutral">Neutral</option>
                    <option value="nej">Nej</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Pris accepterat</label>
                  <select style={inputStyle} value={p.pris_accepterat} onChange={e => uppdateraProvning(index, 'pris_accepterat', e.target.value)}>
                    <option value="ja">Ja</option>
                    <option value="nej">Nej</option>
                    <option value="vet ej">Vet ej</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Kommentar</label>
                <input style={inputStyle} value={p.kommentar} onChange={e => uppdateraProvning(index, 'kommentar', e.target.value)} placeholder="Vad sa inköparen?" />
              </div>
            </div>
          ))}

          <button onClick={läggTillProvning} style={btnGhostStyle}>
            + Lägg till vin
          </button>
        </div>
      )}

      <button
        onClick={spara}
        disabled={sparar}
        style={{
          width: '100%',
          padding: '13px',
          borderRadius: '2px',
          background: TEMA.accent,
          color: TEMA.onAccent,
          fontFamily: "'Inter', sans-serif",
          fontSize: '15px',
          fontWeight: 500,
          border: `1px solid ${TEMA.accent}`,
          cursor: sparar ? 'default' : 'pointer',
          opacity: sparar ? 0.6 : 1,
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={e => { if (!sparar) (e.currentTarget as HTMLElement).style.background = TEMA.accentHover; }}
        onMouseLeave={e => { if (!sparar) (e.currentTarget as HTMLElement).style.background = TEMA.accent; }}
      >
        {sparar ? 'Sparar...' : 'Spara aktivitet'}
      </button>
    </div>
  );
}